<template>
  <div class="fixed inset-0 z-40 flex items-center justify-center" @keyup.esc="handleCancel" tabindex="0">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" @click="handleCancel"></div>
    
    <!-- Modal Content: This is where the original TaskReview content will go -->
    <div class="relative z-50 bg-white p-6 rounded-lg shadow-xl max-w-3xl w-full mx-4 fade-in">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <Loader2 class="w-8 h-8 animate-spin text-cosmicPurple" />
      </div>

      <!-- Review Content -->
      <template v-else>
        <h3 class="text-sm font-medium text-gray-900 mb-3">
          Please review the task:
          <span class="text-sm text-gray-500 font-normal">"{{ pathStepName }}"</span>
        </h3>
        
        <!-- Star Rating -->
        <div class="flex items-center gap-1 mb-4">
          <button
            v-for="star in 5"
            :key="star"
            @click="rating = star"
            class="focus:outline-none"
            :disabled="isSubmitting"
          >
            <Star
              :class="[
                'w-5 h-5 transition-colors',
                star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
              ]"
            />
          </button>
          <span class="ml-2 text-sm text-gray-500">{{ rating }}/5</span>
          <span v-if="attemptedSubmit && !rating && !isSubmitting" class="ml-2 text-xs text-red-500">Please select a rating</span>
        </div>

        <!-- Review Text -->
        <div class="mb-4">
          <textarea
            v-model="reviewText"
            placeholder="Write your review..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            :disabled="isSubmitting"
          ></textarea>
          <span v-if="attemptedSubmit && rating !== 5 && !reviewText.trim() && !isSubmitting" class="text-xs text-red-500">Please write a review</span>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-end gap-2">
          <button
            @click="handleCancel"
            :disabled="isSubmitting"
            class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            @click="handleSubmit"
            :disabled="isSubmitting"
            class="px-4 py-2 bg-cosmicPurple text-white rounded-md text-sm hover:bg-cosmicPurple/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Loader2 v-if="isSubmitting" class="w-4 h-4 mr-2 animate-spin" />
            {{ isSubmitting ? 'Saving...' : 'Save as completed' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Star, Loader2 } from 'lucide-vue-next'
import { useCurriculumStore } from '@/stores/curriculum'
import { useExpeditionsStore } from '@/stores/expeditions'
import { useToast } from 'primevue/usetoast'
import { useRoute } from 'vue-router'
import { useReviewStore } from '@/stores/review'

const props = defineProps<{
  pathStepId: number
  taskId: number
  progressId: number
  pathStepName: string
  onComplete: () => void
  onCancel: () => void
}>()

const emit = defineEmits<{
  (e: 'cancel'): void
}>()

const curriculumStore = useCurriculumStore()
const expeditionsStore = useExpeditionsStore()
const reviewStore = useReviewStore()
const toast = useToast()
const route = useRoute()

const rating = ref(0)
const reviewText = ref('')
const isSubmitting = ref(false)
const attemptedSubmit = ref(false)
const isLoading = ref(true)

// Computed property to check if the current review is the same as the existing one
const isSameReview = computed(() => {
  if (!reviewStore.review) return false
  return rating.value === reviewStore.review.stars_value && 
         reviewText.value.trim() === reviewStore.review.review_text
})

const isValid = computed(() => {
  const hasRating = rating.value > 0
  const hasReview = reviewText.value.trim().length > 0
  
  if (rating.value === 5) {
    return hasRating;
  }
  return hasRating && hasReview
})

// Fetch existing review on mount
onMounted(async () => {
  try {
    await reviewStore.fetchLatestReview(props.taskId)
    if (reviewStore.review) {
      rating.value = reviewStore.review.stars_value
      reviewText.value = reviewStore.review.review_text
    } else {
      rating.value = 0
      reviewText.value = ''
    }
  } catch (error) {
    // If error, reset fields to blank
    // rating.value = 0
    // reviewText.value = ''
    console.error('Error fetching review:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load existing review',
      life: 5000
    })
  } finally {
    isLoading.value = false
  }
})

const handleSubmit = async () => {
  attemptedSubmit.value = true;

  if (!isValid.value) {
    return; 
  }
  
  isSubmitting.value = true;
  
  try {
    // Check if we're in demo mode by checking if the URL includes 'demo'
    const isDemoMode = window.location.pathname.includes('demo');

    // Only submit review if it has changed and we're not in demo mode
    if (!isSameReview.value && !isDemoMode) {
      if (!expeditionsStore.latestExpeditionId) {
        throw new Error('No expedition ID found');
      }
      const reviewSuccess = await curriculumStore.createPathStepReview(
        expeditionsStore.latestExpeditionId,
        props.pathStepId,
        rating.value,
        reviewText.value.trim()
      )

      if (!reviewSuccess) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: curriculumStore.reviewError || 'Failed to save review',
          life: 5000
        })
        isSubmitting.value = false
        return
      }
    }

    // In demo mode, we skip the API call and just show success
    if (isDemoMode) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Task completed and reviewed successfully (Demo Mode)',
        life: 3000
      })
      props.onComplete()
      attemptedSubmit.value = false;
      isSubmitting.value = false;
      return;
    }

    const weekIdFromRoute = route.query.week ? String(route.query.week) : null
    if (!expeditionsStore.latestExpeditionId) {
      throw new Error('No expedition ID found');
    }
    const statusSuccess = await curriculumStore.updateProgressStatus(
      props.progressId,
      'DONE',
      expeditionsStore.latestExpeditionId,
      weekIdFromRoute
    )

    if (statusSuccess) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: isSameReview.value ? 'Task completed successfully' : 'Task completed and reviewed successfully',
        life: 3000
      })
      props.onComplete()
      attemptedSubmit.value = false;
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update task status',
        life: 5000
      })
    }
  } catch (err) {
    console.error('Error completing task:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An unexpected error occurred',
      life: 5000
    })
  } finally {
    isSubmitting.value = false
  }
}

const handleCancel = () => {
  attemptedSubmit.value = false;
  props.onCancel()
}
</script> 